import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DialogShell } from './ui/DialogShell';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { solicitudActividadService } from '../services/solicitudActividadService';
import { recuperarOperacionActividad, observarOperacionActividad, resolverOperacionActividad, VigenciaOperacionActividad } from '../utils/operacionActividadPendiente';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';

/** Sigue visible aunque el formulario original se desmonte o cambie la ruta. */
export function OperacionActividadPendiente() {
  const { authUser } = useAuth();
  return authUser ? <OperacionDelActor key={authUser.id} actor={authUser.id} /> : null;
}

function OperacionDelActor({ actor }: { readonly actor: string }) {
  const [lectura, setLectura] = useState(() => recuperarOperacionActividad(actor));
  const pendiente = lectura.pendiente;
  const [control] = useState(() => new VigenciaOperacionActividad(actor));
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmada, setConfirmada] = useState(false);
  const [mensaje, setMensaje] = useState('');
  useEffect(() => {
    control.activar();
    const leer = () => setLectura(recuperarOperacionActividad(actor));
    leer();
    const dejarDeObservar = observarOperacionActividad(leer);
    return () => { control.invalidar(); dejarDeObservar(); };
  }, [actor, control]);
  const verificar = async (reintentar: boolean) => {
    if (!actor || !pendiente || ocupado) return;
    const captura = control.capturar();
    const contexto = { actor, vigente: () => control.vigente(captura) };
    setOcupado(true); setError(null); setMensaje('');
    try {
      if (reintentar) {
        await solicitudActividadService.ejecutar(pendiente.solicitud, contexto);
        if (!control.vigente(captura)) return;
        setConfirmada(true);
      } else {
        const estado = await solicitudActividadService.consultar(pendiente.solicitud, contexto);
        if (!control.vigente(captura)) return;
        if (estado.estado === 'confirmada') {
          resolverOperacionActividad(actor, pendiente.solicitud.id);
          setConfirmada(true);
        } else {
          setMensaje('Aún no hay confirmación. Puedes verificar otra vez o reintentar la misma operación.');
        }
      }
    } catch (err: unknown) {
      if (!control.vigente(captura)) return;
      setError(mensajeErrorDeNegocio(err) ?? 'No pudimos verificar la operación. Conservamos sus datos para volver a intentarlo.');
    } finally { if (control.vigente(captura)) setOcupado(false); }
  };
  const abierta = !!actor && (confirmada || !!lectura.error || !!error || (!!pendiente && (!pendiente.enviando || ocupado)));
  return (
    <DialogShell isOpen={abierta} onClose={() => { /* Resolver antes de otra operación. */ }}
      size="md" level="nested" header={<h2 className="text-xl font-bold">{confirmada ? 'Operación confirmada' : 'Verificar operación'}</h2>}>
      <div className="space-y-4">
        <p>{confirmada ? 'La operación quedó confirmada. Actualiza la pantalla para ver el resultado.'
          : 'No pudimos confirmar el resultado. Conservamos los datos originales; verifica esta operación antes de registrar otra.'}</p>
        <FormErrorAlert error={lectura.error ?? error} />
        {mensaje && <p role="status" className="text-sm text-slate-600">{mensaje}</p>}
        {confirmada ? <button type="button" className="min-h-11 rounded-lg bg-blue-600 px-4 text-white" onClick={() => window.location.reload()}>Actualizar pantalla</button>
          : pendiente && <div className="flex flex-wrap gap-3">
            <button type="button" disabled={ocupado} className="min-h-11 rounded-lg bg-blue-600 px-4 text-white disabled:opacity-50" onClick={() => { void verificar(false); }}>Verificar resultado</button>
            <button type="button" disabled={ocupado} className="min-h-11 rounded-lg border border-slate-300 px-4 disabled:opacity-50" onClick={() => { void verificar(true); }}>Reintentar la misma operación</button>
          </div>}
        {!pendiente && !confirmada && (lectura.error
          ? <button type="button" className="min-h-11 rounded-lg border border-slate-300 px-4" onClick={() => setLectura(recuperarOperacionActividad(actor))}>Reintentar lectura</button>
          : <button type="button" className="min-h-11 rounded-lg border border-slate-300 px-4" onClick={() => setError(null)}>Volver al formulario</button>)}
      </div>
    </DialogShell>
  );
}
